import PropTypes from 'prop-types';
import { Box, Chip, Typography } from '@mui/material';

const MarketRegimeIndicator = ({ regime, panic_score, fomo_score, t }) => {
  const regimeConfig = {
    panic: {
      color: '#d32f2f',
      bgColor: '#ffebee',
      label: t('sentiment.behavior.regime.panic', 'PANIC'),
      icon: '⚠️',
      description: t('sentiment.behavior.regime.panic_desc', 'Extreme capitulation - high risk/reward opportunity'),
    },
    cautious: {
      color: '#f57c00',
      bgColor: '#fff3e0',
      label: t('sentiment.behavior.regime.cautious', 'CAUTIOUS'),
      icon: '😟',
      description: t('sentiment.behavior.regime.cautious_desc', 'Elevated uncertainty - selective positioning'),
    },
    stable: {
      color: '#1976d2',
      bgColor: '#e3f2fd',
      label: t('sentiment.behavior.regime.stable', 'STABLE'),
      icon: '➡️',
      description: t('sentiment.behavior.regime.stable_desc', 'Neutral conditions - balanced approach'),
    },
    bullish: {
      color: '#388e3c',
      bgColor: '#f1f8e9',
      label: t('sentiment.behavior.regime.bullish', 'BULLISH'),
      icon: '📈',
      description: t('sentiment.behavior.regime.bullish_desc', 'Rising momentum - growth opportunities'),
    },
    euphoric: {
      color: '#d32f2f',
      bgColor: '#ffebee',
      label: t('sentiment.behavior.regime.euphoric', 'EUPHORIC'),
      icon: '🔥',
      description: t('sentiment.behavior.regime.euphoric_desc', 'Extreme optimism - consider profit-taking'),
    },
  };

  const config = regimeConfig[regime] || regimeConfig.stable;

  const contextChips = [];
  if (panic_score >= 0.80) {
    contextChips.push({ label: t('sentiment.behavior.regime.high_panic', 'High Panic'), bg: '#ffcdd2', fg: '#c62828' });
  } else if (panic_score >= 0.65) {
    contextChips.push({ label: t('sentiment.behavior.regime.moderate_panic', 'Moderate Panic'), bg: '#ffe0b2', fg: '#e65100' });
  }
  if (fomo_score >= 0.80) {
    contextChips.push({ label: t('sentiment.behavior.regime.high_fomo', 'High FOMO'), bg: '#ffcdd2', fg: '#c62828' });
  } else if (fomo_score >= 0.65) {
    contextChips.push({ label: t('sentiment.behavior.regime.moderate_fomo', 'Moderate FOMO'), bg: '#c8e6c9', fg: '#2e7d32' });
  }

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        backgroundColor: config.bgColor,
        borderLeft: `3px solid ${config.color}`,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ fontSize: '1.25rem', lineHeight: 1, mt: 0.1 }}>{config.icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 800, color: config.color, fontSize: '0.88rem', lineHeight: 1.2, mb: 0.25 }}>
            {config.label}
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary', lineHeight: 1.35 }}>
            {config.description}
          </Typography>
          {contextChips.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
              {contextChips.map((chip) => (
                <Chip
                  key={chip.label}
                  label={chip.label}
                  size="small"
                  sx={{ backgroundColor: chip.bg, color: chip.fg, fontWeight: 700, height: 22, fontSize: '0.68rem' }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

MarketRegimeIndicator.propTypes = {
  regime: PropTypes.oneOf(['panic', 'cautious', 'stable', 'bullish', 'euphoric']),
  panic_score: PropTypes.number,
  fomo_score: PropTypes.number,
  t: PropTypes.func.isRequired,
};

MarketRegimeIndicator.defaultProps = {
  regime: 'stable',
  panic_score: 0.5,
  fomo_score: 0.5,
};

export default MarketRegimeIndicator;
